import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import SearchBar from 'shared/components/search-bar/search-bar';
import { FilterChipOption } from 'shared/components/table-filter-chips/multi-value-filter-chip/models/filter-chip-option';
import MultiValueFilterChip from 'shared/components/table-filter-chips/multi-value-filter-chip/multi-value-filter-chip';
import OrganisationUnitFilterChip from 'shared/components/table-filter-chips/organisation-unit-filter-chip/organisation-unit-filter-chip';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';

import { useConnectorsContextProvider } from 'features/connectors/context/connectors-provider';
import { ConnectorType } from 'features/connectors/domain/connector';
import { ConnectorFilterValueType } from 'features/connectors/domain/connector-filter';
import { useGetLocations } from 'features/connectors/domain/queries/connector-queries';
import ConnectorMonitoringFilter from 'features/connectors/views/connector-filters/connector-monitoring-filter';
import styles from './connector-filters.module.scss';

export default function ConnectorFilters(): ReactElement {
  const { t } = useTranslation('connectors');

  const { refresh, connectorsFilterHook } = useConnectorsContextProvider();
  const { isSuccess: isLocationsSuccess } = useGetLocations();

  const getTypeFilterValues = (): FilterChipOption[] =>
    Object.entries(ConnectorType)
      .filter(([_, value]) => value !== ConnectorType.Unknown)
      .map(([key, value]) => ({
        key,
        value: [key],
        label: t(`filterValues.type.${value}`),
      }));

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <div className={styles.connectorFilters}>
          <SearchBar onSearchValueChanged={connectorsFilterHook.submitSearch} searchValue={connectorsFilterHook.searchBarValue} />
          <MultiValueFilterChip
            options={getTypeFilterValues()}
            onOptionsSelected={(keys: string[]) => connectorsFilterHook.selectOption(ConnectorFilterValueType.Type, keys)}
            placeHolder={t('filters.type')}
            selectedOptions={getTypeFilterValues().filter((x) =>
              connectorsFilterHook.activeFilters.find((x) => x.filterValueType === ConnectorFilterValueType.Type)?.values.includes(x.key),
            )}
          />

          {isLocationsSuccess && (
            <OrganisationUnitFilterChip
              locationsOnly
              onOptionsSelected={(nodes: SelectedOrganisationTreeNode[]) => {
                connectorsFilterHook.changeSelectedOrganisationUnits(nodes);
                connectorsFilterHook.selectOption(
                  ConnectorFilterValueType.Location,
                  nodes.map((node) => node.id),
                );
              }}
              placeHolder={t('filters.location')}
              selectedOptions={connectorsFilterHook.selectedOrganisationUnits}
            />
          )}

          <div className={styles.clearFilters}>
            <Button variant="text" color="primary" onClick={connectorsFilterHook.clearFilters}>
              {t('filters.clearFilters')}
            </Button>
          </div>
        </div>

        {refresh && (
          <IconButton className={styles.refreshButton} onClick={refresh}>
            <RefreshRounded />
          </IconButton>
        )}
      </div>

      {connectorsFilterHook.monitorFiltersIsSuccess && connectorsFilterHook.monitorFilters.length > 0 && (
        <div className={styles.monitoringFilters}>
          <ConnectorMonitoringFilter.Offline />
          <ConnectorMonitoringFilter.Online />
        </div>
      )}
    </div>
  );
}
